import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Device identity (privacy-compliant): app-issued random id + hash of stable browser characteristics.
// Used only as security evidence to correlate repeated attacks from the same device; never for tracking.
const deviceId = (() => {
  try {
    let id = localStorage.getItem('sobs_device_id');
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, '');
      localStorage.setItem('sobs_device_id', id);
    }
    return id;
  } catch (e) { return ''; }
})();
const deviceFp = (() => {
  try {
    const n = navigator, s = window.screen;
    const src = [n.userAgent, n.language, n.platform, s?.width, s?.height, s?.colorDepth, n.hardwareConcurrency, Intl.DateTimeFormat().resolvedOptions().timeZone, n.maxTouchPoints].join('|');
    let h = 0x811c9dc5;
    for (let i = 0; i < src.length; i++) { h ^= src.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return `fp-${h.toString(16)}-${src.length}`;
  } catch (e) { return ''; }
})();

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (deviceId) config.headers['X-Device-Id'] = deviceId;
  if (deviceFp) config.headers['X-Device-Fp'] = deviceFp;
  return config;
});

// Handle auth errors and provide better error messages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle authentication errors
    const reqUrl = error.config?.url || '';
    const isAuthCall = /\/auth\/(login|verify-otp|resend-otp|forgot-password|reset-password|force-change-password)/.test(reqUrl);
    const onPublicPage = /\/admin\/?$|\/admin\/login|\/admin\/reset-password|\/login|\/verify/.test(window.location.pathname);
    if (error.response?.status === 401 && !isAuthCall) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!onPublicPage) {
        window.location.href = '/admin/login';
      }
    }
    
    // Enhance error message for network errors
    if (!error.response) {
      // No response received - likely network error or timeout
      if (error.code === 'ECONNABORTED') {
        error.message = 'Request timed out. Please try again.';
      } else if (error.message === 'Network Error') {
        error.message = 'Unable to connect to server. Please check your internet connection and try again.';
      }
      console.error('Network/Connection Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  verify2FA: (email, otp) => api.post('/auth/verify-2fa', { email, otp }),
  resendOTP: (email) => api.post('/auth/resend-otp', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  forceChangePassword: (email, currentPassword, newPassword) => api.post('/auth/force-change-password', { email, current_password: currentPassword, new_password: newPassword }),
  getMe: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
};

// User APIs
export const userAPI = {
  list: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
  setPassword: (id, newPassword) => api.post(`/users/${id}/set-password`, { new_password: newPassword }),
  changeOwnPassword: (currentPassword, newPassword) => api.post('/users/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Manifest APIs
export const manifestAPI = {
  list: (queryString = '') => api.get(`/manifests${queryString ? '?' + queryString : ''}`),
  get: (id) => api.get(`/manifests/${id}`),
  create: (data) => api.post('/manifests', data),
  update: (id, data) => api.put(`/manifests/${id}`, data),
  delete: (id) => api.delete(`/manifests/${id}`),
  upload: (formData) => api.post('/manifests/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  extractWithAI: (formData) => api.post('/manifests/extract-ai', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 minutes for AI processing
  }),
  createFromExtraction: (extractionData) => api.post('/manifests/create-from-extraction', extractionData),
  searchAWB: (awbNumber) => api.get(`/manifests/search/awb/${awbNumber}`),
  getAWBSuggestions: (query) => api.get(`/manifests/awb-suggestions?query=${encodeURIComponent(query)}`),
  updateAWBDetails: (manifestId, awbNumber, data) => api.patch(`/manifests/${manifestId}/awbs/${encodeURIComponent(awbNumber)}`, data, {
    timeout: 30000, // 30 seconds timeout
  }),
  splitAWB: (manifestId, awbNumber, parts) => api.post(`/manifests/${manifestId}/awbs/${encodeURIComponent(awbNumber)}/split`, { parts }),
  unsplitAWB: (manifestId, awbNumber) => api.post(`/manifests/${manifestId}/awbs/${encodeURIComponent(awbNumber)}/unsplit`),
  getSplitHistory: (manifestId, awbNumber) => api.get(`/manifests/${manifestId}/awbs/${encodeURIComponent(awbNumber)}/split-history`),
  deleteMotherAWB: (manifestId, awbNumber) => api.delete(`/manifests/${manifestId}/awbs/${encodeURIComponent(awbNumber)}/delete-mother`),
  syncAwbStatuses: () => api.post('/manifests/sync-awb-statuses'),
  syncSpecificAwbs: (awbNumbers) => api.post('/manifests/sync-specific-awbs', awbNumbers),
};

// Inspection APIs
export const inspectionAPI = {
  list: (skip = 0, limit = 50, status = null, search = null) => {
    let url = `/inspections?skip=${skip}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return api.get(url);
  },
  listWithParams: (queryParams) => api.get(`/inspections${queryParams}`),
  get: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post('/inspections', data),
  update: (id, data) => api.put(`/inspections/${id}`, data),
  delete: (id, reason) => api.delete(`/inspections/${id}?reason=${encodeURIComponent(reason)}`),
  uploadDocument: (id, formData) => api.post(`/inspections/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  toggleNcrApproval: (id) => api.post(`/inspections/${id}/ncr-toggle`),
  postClearanceEdit: (id, data) => api.patch(`/inspections/${id}/post-clearance`, data),
  auditTrail: (id) => api.get(`/inspections/${id}/audit-trail`),
};

// Gate Pass APIs
export const gatePassAPI = {
  list: (skip = 0, limit = 50, status = null, search = null) => {
    let url = `/gate-passes?skip=${skip}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return api.get(url);
  },
  listWithParams: (queryParams) => api.get(`/gate-passes${queryParams}`),
  get: (id) => api.get(`/gate-passes/${id}`),
  request: (inspectionId, notes = null) => api.post('/gate-passes', { inspection_id: inspectionId, notes }),
  review: (id, action, reason = null) => api.post(`/gate-passes/${id}/review`, { action, reason }),
  reviewClearance: (id, data) => api.post(`/gate-passes/${id}/review-clearance`, data),
  auditTrail: (id) => api.get(`/gate-passes/${id}/audit-trail`),
  financeReview: (id, financeStatus, explanation = null, feePerKg = null, totalFee = null, feeType = 'per_kg', feePerPiece = null) => 
    api.post(`/gate-passes/${id}/finance-review`, { 
      finance_status: financeStatus, 
      finance_explanation: explanation,
      fee_per_kg_usd: feePerKg,
      fee_per_piece_usd: feePerPiece,
      fee_type: feeType,
      total_fee_usd: totalFee
    }),
  batchFinanceReview: (gatePassIds, financeStatus, explanation = null, feePerKg = null, feePerPiece = null, feeType = 'per_kg') =>
    api.post('/gate-passes/batch-finance-review', {
      gate_pass_ids: gatePassIds,
      finance_status: financeStatus,
      finance_explanation: explanation,
      fee_per_kg_usd: feePerKg,
      fee_per_piece_usd: feePerPiece,
      fee_type: feeType
    }),
  getPendingByConsignee: () => api.get('/gate-passes/pending-by-consignee'),
  updateStatus: (id, status, reason = null) => 
    api.post(`/gate-passes/${id}/update-status`, { status, reason }),
  void: (id, reason) => api.post(`/gate-passes/${id}/void?reason=${encodeURIComponent(reason)}`),
  deleteMultiple: (gatePassIds) => api.post('/gate-passes/delete', { gate_pass_ids: gatePassIds }),
};

// Reports API
export const reportAPI = {
  getAWBClearance: (period, startDate = null, endDate = null) => 
    api.post('/reports/awb-clearance', { period, start_date: startDate, end_date: endDate }),
  getInspectionReport: (period, startDate = null, endDate = null) => 
    api.post('/reports/inspection-report', { period, start_date: startDate, end_date: endDate }),
  getManifestReport: (period, startDate = null, endDate = null) => 
    api.post('/reports/manifest-report', { period, start_date: startDate, end_date: endDate }),
  getMyWork: (date = null) => api.get(`/reports/my-work${date ? `?date=${date}` : ''}`),
  getAllWork: (params = {}) => {
    const queryParts = [];
    if (params.date_from) queryParts.push(`date_from=${params.date_from}`);
    if (params.date_to) queryParts.push(`date_to=${params.date_to}`);
    if (params.user_id) queryParts.push(`user_id=${params.user_id}`);
    if (params.status) queryParts.push(`status=${params.status}`);
    if (params.role) queryParts.push(`role=${params.role}`);
    return api.get(`/reports/all-work${queryParts.length ? '?' + queryParts.join('&') : ''}`);
  },
  getFinance: (params = {}) => {
    const queryParts = [];
    if (params.date_from) queryParts.push(`date_from=${params.date_from}`);
    if (params.date_to) queryParts.push(`date_to=${params.date_to}`);
    if (params.finance_status) queryParts.push(`finance_status=${params.finance_status}`);
    return api.get(`/reports/finance${queryParts.length ? '?' + queryParts.join('&') : ''}`);
  },
  getUserPerformance: (period, startDate = null, endDate = null) => 
    api.post('/reports/user-performance', { period, start_date: startDate, end_date: endDate }),
  getMedicalReport: (period, startDate = null, endDate = null) =>
    api.post('/reports/medical-report', { period, start_date: startDate, end_date: endDate }),
  getNonMedicalReport: (period, startDate = null, endDate = null) =>
    api.post('/reports/non-medical-report', { period, start_date: startDate, end_date: endDate }),
  getStatusReport: (period, startDate = null, endDate = null, groupBy = null, manifestFilter = null, airlineFilter = null) =>
    api.post('/reports/status-report', { 
      period, 
      start_date: startDate, 
      end_date: endDate,
      group_by: groupBy,
      manifest_filter: manifestFilter,
      airline_filter: airlineFilter
    }),
};

// Security Settings API
export const settingsAPI = {
  getSecurity: () => api.get('/settings/security'),
  updateSecurity: (data) => api.put('/settings/security', data),
};

// Public Verification API (no auth)
export const verifyGatePass = (token) => axios.get(`${API_URL}/verify/${token}`);

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Goods Categories API
export const getGoodsCategories = () => api.get('/goods-categories');

// Audit Logs API
export const auditAPI = {
  list: (params = {}) => api.get('/audit-logs', { params }),
  securitySummary: () => api.get('/audit-logs/security-summary'),
  getIpWarnings: () => api.get('/audit-logs/ip-warnings'),
  getUserRoles: () => api.get('/audit-logs/user-roles'),
  getUsers: () => api.get('/audit-logs/users'),
  userActivity: (userId, params = {}) => api.get(`/audit-logs/user-activity/${userId}`, { params }),
  userSecurity: (userId) => api.get(`/audit-logs/user-security/${userId}`),
};

// Admin API
export const adminAPI = {
  syncAwbStatuses: () => api.post('/admin/sync-awb-statuses'),
  getGatePassCounter: () => api.get('/admin/gate-pass-counter'),
  resetGatePassCounter: (startFrom = 1) => api.post('/admin/gate-pass-counter/reset', { start_from: startFrom }),
};

// System Setup API
export const setupAPI = {
  init: () => api.post('/setup/init'),
};

// Health Check
export const healthCheck = () => api.get('/health');

// Trash Bin API - Super Admin only
export const trashAPI = {
  list: (itemType = null) => {
    let url = '/trash';
    if (itemType) url += `?item_type=${itemType}`;
    return api.get(url);
  },
  restore: (itemType, itemId) => api.post(`/trash/restore/${itemType}/${itemId}`),
  deletionFlags: (status = 'flagged') => api.get('/audit/deletion-flags', { params: { status } }),
  permanentDelete: (itemType, itemId) => api.delete(`/trash/permanent/${itemType}/${itemId}`),
  cleanup: () => api.post('/trash/cleanup'),
};

export default api;

// Advanced protection: sessions, lockouts, IP blocks, alerts
export const securityAPI = {
  protection: () => api.get('/security/protection'),
  unblockIp: (ip, reason, password) => api.post('/security/protection/unblock-ip', { ip, reason, password }),
  unblockDevice: (device_key, reason, password) => api.post('/security/protection/unblock-device', { device_key, reason, password }),
  blockIp: (data) => api.post('/security/protection/block-ip', data),
  ipEvidence: (ip) => api.get(`/security/protection/ip/${encodeURIComponent(ip)}`),
  addTrustedIp: (data) => api.post('/security/protection/trusted-ips', data),
  removeTrustedIp: (ip) => api.delete(`/security/protection/trusted-ips/${encodeURIComponent(ip)}`),
  incidents: (params) => api.get('/security/protection/incidents', { params }),
  reviewIncident: (logId, status, notes) => api.post(`/security/protection/incidents/${logId}/review`, { status, notes }),
  auditIntegrity: (limit = 500) => api.get('/security/protection/audit-integrity', { params: { limit } }),
  setMfaStrict: (enabled, password) => api.post('/security/protection/mfa-strict', { enabled, password }),
  mfaStatus: () => api.get('/auth/mfa/status'),
  totpSetup: () => api.post('/auth/mfa/totp/setup'),
  totpConfirm: (code) => api.post('/auth/mfa/totp/confirm', { code }),
  totpDisable: (password) => api.post('/auth/mfa/totp/disable', { password }),
  adminResetMfa: (userId, password) => api.post(`/users/${userId}/mfa/reset`, { password }),
  wafTest: () => api.get('/security/protection/waf-test'),
  unlockAccount: (email) => api.post('/security/protection/unlock-account', { email }),
  markAlertsRead: () => api.post('/security/protection/alerts/mark-read'),
  mySessions: () => api.get('/auth/sessions'),
  revokeSession: (sid) => api.delete(`/auth/sessions/${sid}`),
  revokeOtherSessions: () => api.post('/auth/sessions/revoke-others'),
  revokeUserSessions: (userId) => api.post(`/users/${userId}/revoke-sessions`),
};
